import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Row } from 'antd';
import { Formik } from 'formik';
import { Form, Select, Input } from 'formik-antd';
import moment from 'moment';
import * as Yup from 'yup';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, FormInput, CustomInput, Button } from 'atoms';
import { DatePickers } from 'utils/commonStyled';
import UploadWithPreview from '../common/UploadWithPreview';
import { fetchAdhocRequestDetail, fetchAdhocRequestDetailV2 } from 'store/advancePaymentSlice';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';
import NodeSequenceModal from 'components/common/NodeSequenceModal/NodeSequenceModal';
import { getUIVersion } from 'utils/router-static-data';

const FormContainer = styled(Box)`
	max-width: 1200px;
	margin: 32px auto;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
	padding: 40px 60px 32px 60px;
`;
const FormButtonContainer = styled.div`
	justify-content: flex-end;
	display: flex;
	margin-top: 18px;
`;
const FormPrimaryButton = styled(Button)`
	background: #18405d;
	color: #fff;
	border-radius: 4px;
	padding: 7px 24px;
	font-size: 15px;
	font-weight: 500;
`;
const CompactRow = styled(Row)`
	margin-bottom: 10px !important;
`;
const CompactCol = styled(Col)`
	margin-bottom: 0 !important;
`;
const LocationFieldRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const PincodeInput = styled.div`
	width: 120px;
	margin-bottom: 0 !important;
`;
const CityStateInput = styled.div`
	flex: 1;
	margin-bottom: 0 !important;
`;
const MarginRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const MarginInput = styled.div`
	flex: 1;
	margin-bottom: 0 !important;
`;
const PercentInput = styled.div`
	width: 120px;
	margin-bottom: 0 !important;
`;
const RouteContainer = styled.div`
	margin-bottom: 10px;
`;
const RouteFieldsRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;
const RouteFieldWrapper = styled.div`
	flex: 1;
	min-width: 0;
`;
const StopsIndicator = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	color: #888;
	font-size: 13px;
	white-space: nowrap;
	padding-top: 28px;
`;
const SelectNodeLink = styled.button`
	border: none;
	background: transparent;
	color: #1677ff;
	font-size: 14px;
	font-weight: 500;
	padding: 0;
	cursor: pointer;
	text-decoration: underline;
	text-align: right;
	display: block;
	margin-top: 4px;
	margin-left: auto;

	&:hover {
		text-decoration: underline;
	}

	&:disabled {
		color: #94a3b8;
		cursor: not-allowed;
		text-decoration: none;
	}
`;

const normalizeAddress = (address = '') => address.trim().toLowerCase();

const nodeListAfterRoundTripFromStart = (prevNodes, startAddress) => {
	const startAddr = startAddress || '';
	if (!prevNodes?.length) {
		return [
			{ address: startAddr, type: 'start' },
			{ address: startAddr, type: 'end' },
		];
	}
	const next = [...prevNodes];
	if (next[0]) {
		next[0] = { ...next[0], address: startAddr };
	}
	next[next.length - 1] = {
		...next[next.length - 1],
		address: startAddr,
	};
	return next;
};

const filterSelectOption = (input, option) =>
	option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;

const handleDecimalInputChange = (fieldName, setFieldValue) => (e) => {
	const { value } = e.target;
	if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
		setFieldValue(fieldName, value);
	}
};

const buildNodesForModal = (values, nodeSequence) =>
	nodeSequence.length > 0
		? nodeSequence
		: [
				{ address: values.start_address || '', type: 'start' },
				{ address: '', type: 'intermediary' },
				{ address: values.destination_address || '', type: 'end' },
		  ];

const getUpdatedStartNodes = (prev, truncatedAddress, isRoundTrip, destinationAddress) => {
	if (prev.length > 0) {
		const updated = [...prev];
		updated[0] = { ...updated[0], address: truncatedAddress };
		if (isRoundTrip) {
			updated[updated.length - 1] = {
				...updated[updated.length - 1],
				address: truncatedAddress,
			};
		}
		return updated;
	}

	return [
		{ address: truncatedAddress, type: 'start' },
		{
			address: isRoundTrip ? truncatedAddress : destinationAddress || '',
			type: 'end',
		},
	];
};

const getUpdatedDestinationNodes = (prev, startAddress, truncatedAddress) => {
	if (prev.length > 0) {
		const updated = [...prev];
		updated[updated.length - 1] = {
			...updated[updated.length - 1],
			address: truncatedAddress,
		};
		return updated;
	}

	return [
		{ address: startAddress || '', type: 'start' },
		{ address: truncatedAddress, type: 'end' },
	];
};

const getIntermediaryAddresses = (nodes) =>
	nodes
		.filter((node) => node.type === 'intermediary' && node.address)
		.map((node) => node.address);

const syncStartAddress = ({ data, values, setFieldValue, setNodeSequence }) => {
	const truncatedAddress = truncateAddressToCity(data.address, data.city || '');
	const isRoundTrip = values.route_type === 'round_trip';

	setFieldValue('start_address', truncatedAddress);
	if (isRoundTrip) {
		setFieldValue('destination_address', truncatedAddress);
	}

	setNodeSequence((prev) =>
		getUpdatedStartNodes(prev, truncatedAddress, isRoundTrip, values.destination_address)
	);
};

const syncDestinationAddress = ({ data, values, setFieldValue, setNodeSequence }) => {
	const truncatedAddress = truncateAddressToCity(data.address, data.city || '');
	setFieldValue('destination_address', truncatedAddress);
	setNodeSequence((prev) =>
		getUpdatedDestinationNodes(prev, values.start_address, truncatedAddress)
	);
};

const syncConfirmedNodeSequence = ({
	confirmedNodes,
	setFieldValue,
	setNodeSequence,
	setNodeSequenceModalVisible,
}) => {
	setNodeSequence(confirmedNodes);

	if (confirmedNodes.length > 0) {
		setFieldValue('start_address', confirmedNodes[0].address || '');
	}
	if (confirmedNodes.length > 1) {
		setFieldValue(
			'destination_address',
			confirmedNodes[confirmedNodes.length - 1].address || ''
		);
	}

	setFieldValue('intermediary_addresses', getIntermediaryAddresses(confirmedNodes));
	setNodeSequenceModalVisible(false);
};

const renderStopsIndicator = (stopsCount) =>
	stopsCount > 0 ? (
		<StopsIndicator>
			{'··· '}
			{stopsCount}
			{' stop'}
			{stopsCount > 1 ? 's' : ''}
			{' ···>'}
		</StopsIndicator>
	) : null;

function renderBasicDetailsSection({
	clientData,
	projectData,
	vendorsData,
	vehicleTypes,
	isDisabled,
	isV2UILocal,
	onClientChange,
	onProjectChange,
	onVehicleChange,
	setFieldValue,
}) {
	return (
		<>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="client_id" label="Client Name *">
						<Select
							name="client_id"
							showSearch
							options={clientData}
							placeholder="Select Client"
							disabled={isDisabled}
							filterOption={filterSelectOption}
							onChange={(e) => onClientChange(e, setFieldValue)}
						/>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="project_id" label="Project Name *">
						<Select
							name="project_id"
							showSearch
							options={projectData}
							placeholder="Select Project"
							disabled={isDisabled}
							filterOption={filterSelectOption}
							onChange={(e) => onProjectChange(e, setFieldValue)}
						/>
					</FormInput>
				</CompactCol>
			</CompactRow>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="vendor_id" label="Vendor Name *">
						<Select
							name="vendor_id"
							showSearch
							options={vendorsData}
							placeholder="Select Vendor"
							disabled={isDisabled}
							filterOption={filterSelectOption}
						/>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="vehicle_type" label="Vehicle Type *">
						<Select
							name="vehicle_type"
							showSearch
							options={vehicleTypes}
							placeholder="Select Vehicle"
							disabled={isDisabled}
							filterOption={filterSelectOption}
							onChange={
								isV2UILocal
									? (vehicleId) => onVehicleChange(vehicleId, setFieldValue)
									: undefined
							}
						/>
					</FormInput>
				</CompactCol>
			</CompactRow>
		</>
	);
}

function renderRouteTypeSection({
	isV2UILocal,
	isDisabled,
	values,
	setFieldValue,
	setNodeSequence,
	nodeSequence,
}) {
	if (!isV2UILocal) {
		return (
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="movement_type" label="Movement Type *">
						<CustomInput name="movement_type" value="Intercity" disabled />
					</FormInput>
				</CompactCol>
				<CompactCol span={12} />
			</CompactRow>
		);
	}

	const handleRouteTypeChange = async (routeType) => {
		if (!routeType) return;

		await setFieldValue('route_type', routeType);

		if (routeType === 'round_trip') {
			const startAddr = values.start_address || '';
			await setFieldValue('destination_address', startAddr);
			await setFieldValue('destination_pincode', values.source_pincode || '');
			const nextNodes = nodeListAfterRoundTripFromStart(nodeSequence, startAddr);
			setNodeSequence(nextNodes);
			await setFieldValue('intermediary_addresses', getIntermediaryAddresses(nextNodes));
			return;
		}

		// Switching away from round_trip — clear the auto-filled destination
		await setFieldValue('destination_address', '');
		await setFieldValue('destination_pincode', '');
		setNodeSequence((prev) => {
			if (prev.length === 0) return prev;
			const updated = [...prev];
			updated[updated.length - 1] = {
				...updated[updated.length - 1],
				address: '',
			};
			return updated;
		});
	};

	return (
		<CompactRow gutter={16}>
			<CompactCol span={12}>
				<FormInput name="movement_type" label="Movement Type *">
					<CustomInput name="movement_type" value="Intercity" disabled />
				</FormInput>
			</CompactCol>
			<CompactCol span={12}>
				<FormInput name="route_type" label="Route Type *">
					<Select
						name="route_type"
						options={[
							{ value: 'one_way', label: 'One Way' },
							{ value: 'round_trip', label: 'Round Trip' },
						]}
						placeholder="Select Route Type"
						disabled={isDisabled}
						onChange={handleRouteTypeChange}
					/>
				</FormInput>
			</CompactCol>
		</CompactRow>
	);
}

function renderV2RouteSection({
	values,
	isDisabled,
	nodeSequence,
	stopsCount,
	nodeSequenceModalVisible,
	setFieldValue,
	setNodeSequence,
	setNodeSequenceModalVisible,
}) {
	return (
		<>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<RouteContainer>
						<div style={{ fontWeight: 500, marginBottom: 8 }}>Route</div>
						<RouteFieldsRow>
							<RouteFieldWrapper>
								<FormInput name="start_address" label=" ">
									<AddressPickerV2
										value={values.start_address || ''}
										onChange={(data) =>
											syncStartAddress({
												data,
												values,
												setFieldValue,
												setNodeSequence,
											})
										}
										placeholder="Select Source"
										disabled={isDisabled || !values.route_type}
									/>
								</FormInput>
							</RouteFieldWrapper>
							{renderStopsIndicator(stopsCount)}
							<RouteFieldWrapper>
								<FormInput name="destination_address" label=" ">
									<AddressPickerV2
										value={values.destination_address || ''}
										onChange={(data) =>
											syncDestinationAddress({
												data,
												values,
												setFieldValue,
												setNodeSequence,
											})
										}
										placeholder="Select Destination"
										disabled={
											isDisabled ||
											!values.route_type ||
											values.route_type === 'round_trip'
										}
									/>
								</FormInput>
							</RouteFieldWrapper>
						</RouteFieldsRow>
						<SelectNodeLink
							type="button"
							onClick={() => {
								if (isDisabled || !values.route_type) return;
								setNodeSequenceModalVisible(true);
							}}
							disabled={isDisabled || !values.route_type}
						>
							Select Node Sequence
						</SelectNodeLink>
					</RouteContainer>
				</CompactCol>
			</CompactRow>
			<NodeSequenceModal
				visible={nodeSequenceModalVisible}
				onClose={() => setNodeSequenceModalVisible(false)}
				routeType={values.route_type}
				onConfirm={(confirmedNodes) =>
					syncConfirmedNodeSequence({
						confirmedNodes,
						setFieldValue,
						setNodeSequence,
						setNodeSequenceModalVisible,
					})
				}
				initialNodes={buildNodesForModal(values, nodeSequence)}
				disabled={isDisabled || !values.route_type}
			/>
		</>
	);
}

function renderLegacyLocationSection({
	isDisabled,
	sourceCity,
	destCity,
	onPincodeChange,
	setFieldValue,
}) {
	return (
		<CompactRow gutter={16}>
			<CompactCol span={12}>
				<FormInput name="source_pincode" label="Source Location *">
					<LocationFieldRow>
						<PincodeInput>
							<CustomInput
								name="source_pincode"
								placeholder="Pincode"
								maxLength={6}
								disabled={isDisabled}
								onChange={(e) => {
									setFieldValue('source_pincode', e.target.value);
									onPincodeChange(e.target.value, 'source', setFieldValue);
								}}
							/>
						</PincodeInput>
						<CityStateInput>
							<CustomInput
								name="source_city_state"
								value={sourceCity}
								placeholder="City, State"
								disabled
							/>
						</CityStateInput>
					</LocationFieldRow>
				</FormInput>
			</CompactCol>
			<CompactCol span={12}>
				<FormInput name="destination_pincode" label="Destination Location *">
					<LocationFieldRow>
						<PincodeInput>
							<CustomInput
								name="destination_pincode"
								placeholder="Pincode"
								maxLength={6}
								disabled={isDisabled}
								onChange={(e) => {
									setFieldValue('destination_pincode', e.target.value);
									onPincodeChange(e.target.value, 'destination', setFieldValue);
								}}
							/>
						</PincodeInput>
						<CityStateInput>
							<CustomInput
								name="destination_city_state"
								value={destCity}
								placeholder="City, State"
								disabled
							/>
						</CityStateInput>
					</LocationFieldRow>
				</FormInput>
			</CompactCol>
		</CompactRow>
	);
}

function renderTripMetaSection({
	isDisabled,
	supervisorData,
	temperatureLogsOptions,
	businessUnitName,
	disabledDate,
}) {
	return (
		<>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="trip_date" label="Trip Date *">
						<DatePickers
							name="trip_date"
							showTime
							format="MM/DD/YYYY h:mm A"
							disabledDate={disabledDate}
							disabled={isDisabled}
						/>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="temperature_logs" label="Temperature Logs *">
						<Select
							name="temperature_logs"
							options={temperatureLogsOptions}
							placeholder="Select"
							disabled={isDisabled}
						/>
					</FormInput>
				</CompactCol>
			</CompactRow>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="business_unit" label="Business Unit *">
						<CustomInput
							name="business_unit"
							value={businessUnitName || ''}
							placeholder="Business Unit"
							disabled
						/>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="supervisor_id" label="Supervisor Name *">
						<Select
							name="supervisor_id"
							showSearch
							options={supervisorData}
							placeholder="Select Supervisor"
							filterOption={filterSelectOption}
							disabled={isDisabled}
						/>
					</FormInput>
				</CompactCol>
			</CompactRow>
		</>
	);
}

function renderUploadSection({
	isDisabled,
	uploadingProof,
	uploadErrorProof,
	values,
	touched,
	errors,
	onProofUpload,
	setFieldValue,
}) {
	return (
		<>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<div style={{ fontWeight: 500, marginBottom: 8 }}>
						Upload Proof of Request *
					</div>
					<UploadWithPreview
						beforeUpload={async (file) => onProofUpload(file, setFieldValue)}
						uploading={uploadingProof}
						disabled={isDisabled}
						uploadError={uploadErrorProof}
						uploadedFile={
							values.proof_of_request && values.proof_of_request.length > 0
								? values.proof_of_request[0]
								: null
						}
						onFileRemove={() => setFieldValue('proof_of_request', [])}
						buttonText="Upload"
						accept={['.pdf', '.jpg', '.jpeg', '.png']}
						entity="trip"
					/>
					{touched.proof_of_request && errors.proof_of_request && (
						<div style={{ color: 'red', marginTop: 4 }}>{errors.proof_of_request}</div>
					)}
				</CompactCol>
				<CompactCol span={12}>
					{/* <FormInput
						name="proforma_invoice_number"
						label="Pro-Forma Invoice Number"
					>
						<CustomInput
							name="proforma_invoice_number"
							placeholder="Pro-Forma Invoice Number"
						/>
					</FormInput> */}
				</CompactCol>
			</CompactRow>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					{/* <div style={{ fontWeight: 500, marginBottom: 8 }}>
						Upload Vendor Pro-Forma Invoice
					</div>
					<UploadWithPreview
						beforeUpload={async (file) =>
							onProformaUpload(file, setFieldValue)
						}
						uploading={uploadingProforma}
						uploadError={uploadErrorProforma}
						uploadedFile={
							values.proforma_invoice &&
							values.proforma_invoice.length > 0
								? values.proforma_invoice[0]
								: null
						}
						onFileRemove={() => setFieldValue('proforma_invoice', [])}
						buttonText="Upload"
						accept={['.pdf', '.jpg', '.jpeg', '.png']}
						entity="trip"
					/>
					{touched.proforma_invoice && errors.proforma_invoice && (
						<div style={{ color: 'red', marginTop: 4 }}>
							{errors.proforma_invoice}
						</div>
					)} */}
				</CompactCol>
				<CompactCol span={12} />
			</CompactRow>
		</>
	);
}

function renderFinancialSection({
	values,
	isDisabled,
	margin,
	marginPercent,
	advancePercent,
	setFieldValue,
}) {
	return (
		<>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="client_rate" label="Client Rate *">
						<CustomInput
							name="client_rate"
							placeholder="Client Rate"
							type="number"
							step="0.01"
							min="0"
							disabled={isDisabled}
							value={values.client_rate}
							onChange={handleDecimalInputChange('client_rate', setFieldValue)}
						/>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="vendor_rate" label="Vendor Rate *">
						<CustomInput
							name="vendor_rate"
							placeholder="Vendor Rate"
							type="number"
							step="0.01"
							min="0"
							disabled={isDisabled}
							value={values.vendor_rate}
							onChange={handleDecimalInputChange('vendor_rate', setFieldValue)}
						/>
					</FormInput>
				</CompactCol>
			</CompactRow>
			<CompactRow gutter={16}>
				<CompactCol span={12}>
					<FormInput name="margin" label="Margin *">
						<MarginRow>
							<MarginInput>
								<CustomInput name="margin" value={`₹${margin}`} disabled />
							</MarginInput>
							<PercentInput>
								<CustomInput
									name="margin_percent"
									value={`${marginPercent}%`}
									disabled
								/>
							</PercentInput>
						</MarginRow>
					</FormInput>
				</CompactCol>
				<CompactCol span={12}>
					<FormInput name="advance_amount" label="Advance *">
						<MarginRow>
							<MarginInput>
								<CustomInput
									name="advance_amount"
									placeholder="Advance"
									type="number"
									step="0.01"
									min="0"
									disabled={isDisabled}
									value={values.advance_amount}
									onChange={handleDecimalInputChange(
										'advance_amount',
										setFieldValue
									)}
								/>
							</MarginInput>
							<PercentInput>
								<CustomInput
									name="advance_percent"
									value={
										values.advance_amount && values.vendor_rate
											? `${advancePercent}%`
											: '%'
									}
									disabled
								/>
							</PercentInput>
						</MarginRow>
					</FormInput>
				</CompactCol>
			</CompactRow>
			<CompactRow gutter={16}>
				<CompactCol span={24}>
					<FormInput name="remarks" label="Remarks">
						<>
							<Input.TextArea
								name="remarks"
								placeholder="Enter remarks"
								maxLength={250}
								disabled={false}
								rows={4}
								style={{ resize: 'none' }}
							/>
							<div
								style={{
									textAlign: 'right',
									fontSize: 12,
									color: '#6b7280',
									marginTop: 4,
								}}
							>
								{values.remarks?.length || 0}/250
							</div>
						</>
					</FormInput>
				</CompactCol>
			</CompactRow>
		</>
	);
}

function renderFormActions({ navigate, isSubmitting, isEditMode }) {
	return (
		<FormButtonContainer>
			<FormPrimaryButton type="button" onClick={() => navigate('/trips/adhoc-request')}>
				Cancel
			</FormPrimaryButton>
			<FormPrimaryButton type="submit" disabled={isSubmitting} loading={isSubmitting}>
				{isEditMode ? 'Edit' : 'Create'}
			</FormPrimaryButton>
		</FormButtonContainer>
	);
}

function renderAdhocRequestFormFields(props) {
	const {
		values,
		errors,
		touched,
		setFieldValue,
		isSubmitting,
		nodeSequence,
		setNodeSequence,
		nodeSequenceModalVisible,
		setNodeSequenceModalVisible,
		isV2UILocal,
		isDisabled,
		clientData,
		projectData,
		vendorsData,
		vehicleTypes,
		supervisorData,
		sourceCity,
		destCity,
		businessUnitName,
		uploadingProof,
		uploadErrorProof,
		onClientChange,
		onProjectChange,
		onPincodeChange,
		onProofUpload,
		onVehicleChange,
		navigate,
		isEditMode,
		temperatureLogsOptions,
		disabledDate,
		calculateMargin,
		calculateAdvancePercent,
	} = props;

	const { margin, marginPercent } = calculateMargin(values.client_rate, values.vendor_rate);
	const advancePercent = calculateAdvancePercent(values.advance_amount, values.vendor_rate);
	const stopsCount = nodeSequence.filter(
		(node) => node.type === 'intermediary' && node.address
	).length;

	return (
		<Form>
			{renderBasicDetailsSection({
				clientData,
				projectData,
				vendorsData,
				vehicleTypes,
				isDisabled,
				isV2UILocal,
				onClientChange,
				onProjectChange,
				onVehicleChange,
				setFieldValue,
			})}
			{renderRouteTypeSection({
				isV2UILocal,
				isDisabled,
				values,
				setFieldValue,
				setNodeSequence,
				nodeSequence,
			})}
			{isV2UILocal
				? renderV2RouteSection({
						values,
						isDisabled,
						nodeSequence,
						stopsCount,
						nodeSequenceModalVisible,
						setFieldValue,
						setNodeSequence,
						setNodeSequenceModalVisible,
				  })
				: renderLegacyLocationSection({
						isDisabled,
						sourceCity,
						destCity,
						onPincodeChange,
						setFieldValue,
				  })}
			{renderTripMetaSection({
				isDisabled,
				supervisorData,
				temperatureLogsOptions,
				businessUnitName,
				disabledDate,
			})}
			{renderUploadSection({
				isDisabled,
				uploadingProof,
				uploadErrorProof,
				values,
				touched,
				errors,
				onProofUpload,
				setFieldValue,
			})}
			{renderFinancialSection({
				values,
				isDisabled,
				margin,
				marginPercent,
				advancePercent,
				setFieldValue,
			})}
			{renderFormActions({ navigate, isSubmitting, isEditMode })}
		</Form>
	);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function AdhocRequestForm({
	formData = {},
	clientData = [],
	projectData = [],
	vendorsData = [],
	vehicleTypes = [],
	supervisorData = [],
	sourceCity,
	destCity,
	businessUnitName,
	uploadingProof,
	uploadErrorProof,
	// uploadingProforma,
	// uploadErrorProforma,
	formRef,
	onClientChange,
	onProjectChange,
	onPincodeChange,
	onProofUpload,
	onVehicleChange,
	// onProformaUpload,
	onSubmit,
	navigate,
	isEditMode = false,
	requestData = null, // Pass requestData from parent to get status
	isV2UI = false, // Pass UI version from parent
}) {
	const initialValues = React.useMemo(
		// eslint-disable-next-line sonarjs/cognitive-complexity
		() => ({
			client_id: formData?.client_id || '',
			project_id: formData?.project_id || '',
			route_id: formData?.route_id || '',
			vendor_id: formData?.vendor_id || '',
			vehicle_type: formData?.vehicle_type || '',
			movement_type: 'Intercity',
			route_type: formData?.route_type,
			start_address: formData?.start_address || '',
			destination_address: formData?.destination_address || '',
			source_pincode: formData?.source_pincode || '',
			destination_pincode: formData?.destination_pincode || '',
			trip_date: formData?.trip_date || undefined,
			temperature_logs: formData?.temperature_logs || false,
			supervisor_id: formData?.supervisor_id || '',
			proof_of_request: formData?.proof_of_request || [],
			proforma_invoice_number: formData?.proforma_invoice_number || '',
			proforma_invoice: formData?.proforma_invoice || [],
			client_rate: formData?.client_rate || '',
			vendor_rate: formData?.vendor_rate || '',
			advance_amount: formData?.advance_amount || '',
			intermediary_addresses: formData?.intermediary_addresses || [],
			node_sequence: formData?.node_sequence || [],
			remarks: formData?.remarks || '',
		}),
		[formData]
	);

	const [searchParams] = useSearchParams();
	const id = searchParams.get('id');
	const [tripStatus, setTripStatus] = React.useState('');
	const dispatch = useDispatch();
	const uiVersion = getUIVersion();
	const isV2UILocal = isV2UI || uiVersion === 'new';
	const [nodeSequenceModalVisible, setNodeSequenceModalVisible] = React.useState(false);
	const [nodeSequence, setNodeSequence] = React.useState([]);

	React.useEffect(() => {
		if (isV2UILocal) {
			loadGoogleMapsV2().catch((err) => {
				console.error('Failed to load Google Maps:', err);
			});
		}
	}, [isV2UILocal]);

	React.useEffect(() => {
		if (!isV2UILocal) {
			setNodeSequence([]);
			return;
		}

		if (Array.isArray(formData?.node_sequence) && formData.node_sequence.length > 0) {
			setNodeSequence(formData.node_sequence);
			return;
		}

		const startNode = formData?.start_address
			? [{ address: formData.start_address, type: 'start' }]
			: [];
		const intermediaryNodes = Array.isArray(formData?.intermediary_addresses)
			? formData.intermediary_addresses
					.filter(Boolean)
					.map((address) => ({ address, type: 'intermediary' }))
			: [];
		const endNode = formData?.destination_address
			? [{ address: formData.destination_address, type: 'end' }]
			: [];

		setNodeSequence([...startNode, ...intermediaryNodes, ...endNode]);
	}, [formData, isV2UILocal]);

	// Get status from requestData if available, otherwise fetch it
	React.useEffect(() => {
		if (requestData?.status) {
			// Use status from parent component if available
			setTripStatus(requestData.status);
		} else if (id && isEditMode) {
			// Only fetch if we don't have requestData and we're in edit mode
			const fetchDetails = async () => {
				try {
					const response = isV2UILocal
						? await dispatch(fetchAdhocRequestDetailV2({ id }))
						: await dispatch(fetchAdhocRequestDetail({ id }));
					const tripStatusValue = response?.rows?.[0]?.status;
					if (tripStatusValue) {
						setTripStatus(tripStatusValue);
					}
				} catch (error) {
					console.error('Error fetching request status:', error);
				}
			};
			fetchDetails();
		}
	}, [id, isEditMode, requestData, dispatch, isV2UILocal]);

	// Only disable fields if status is 'Approved' or 'Trip Created'
	// In edit mode, allow editing unless status blocks it
	// Only disable if we have a status AND it's one of the blocking statuses
	const isDisabled =
		isEditMode && tripStatus && (tripStatus === 'Approved' || tripStatus === 'Trip Created');

	const MAX_DECIMAL_PLACES_MSG = 'can have maximum 2 decimal places';
	const DECIMAL_PLACES_TEST_NAME = 'decimal-places';

	// Helper function to validate decimal places
	const validateDecimalPlaces = (value) => {
		if (!value) return true;
		const decimalPlaces = value.toString().split('.')[1];
		return !decimalPlaces || decimalPlaces.length <= 2;
	};

	const validationSchema = Yup.object({
		client_id: Yup.string().required('Client name is required').label('Client Name'),
		project_id: Yup.string().required('Project name is required').label('Project Name'),
		route_id: Yup.string().optional().label('Route'),
		vendor_id: Yup.string().required('Vendor name is required').label('Vendor Name'),
		vehicle_type: Yup.string().required('Vehicle type is required').label('Vehicle Type'),
		movement_type: Yup.string().label('Movement Type'),
		route_type: isV2UILocal
			? Yup.string().required('Route type is required').label('Route Type')
			: Yup.string().label('Route Type'),
		start_address: isV2UILocal
			? Yup.string()
					.required('Start address is required')
					.test(
						'different-from-destination',
						'Start and destination address cannot be same',
						function validateDifferentStartAddress(value, context) {
							const { destination_address, route_type } = context.parent || {};
							if (route_type === 'round_trip') return true;
							if (!value || !destination_address) return true;
							return (
								normalizeAddress(value) !== normalizeAddress(destination_address)
							);
						}
					)
					.label('Start Address')
			: Yup.string().label('Start Address'),
		destination_address: isV2UILocal
			? Yup.string()
					.required('Destination address is required')
					.test(
						'different-from-source',
						'Source and destination address cannot be same',
						function validateDifferentDestinationAddress(value, context) {
							const { start_address, route_type } = context.parent || {};
							if (route_type === 'round_trip') return true;
							if (!value || !start_address) return true;
							return normalizeAddress(value) !== normalizeAddress(start_address);
						}
					)
					.label('Destination Address')
			: Yup.string().label('Destination Address'),
		source_pincode: isV2UILocal
			? Yup.string().label('Source Pincode')
			: Yup.string()
					.required('Source pincode is required')
					.matches(/^\d{6}$/, 'Enter a valid 6 digit pincode')
					.label('Source Pincode'),
		destination_pincode: isV2UILocal
			? Yup.string().label('Destination Pincode')
			: Yup.string()
					.required('Destination pincode is required')
					.matches(/^\d{6}$/, 'Enter a valid 6 digit pincode')
					.test(
						'different-from-source',
						'Destination pincode must be different from source pincode',
						function validateDifferentPincode(value, context) {
							const { source_pincode } = context.parent || {};
							if (!value || !source_pincode) return true;
							return value !== source_pincode;
						}
					)
					.label('Destination Pincode'),
		trip_date: Yup.mixed()
			.required('Trip date is required')
			.test('is-valid-date', 'Invalid trip date', function validateDate(value) {
				return value && (value instanceof Date || moment(value).isValid());
			})
			.label('Trip Date'),
		temperature_logs: Yup.boolean().required().label('Temperature Logs'),
		supervisor_id: Yup.string()
			.required('Supervisor name is required')
			.label('Supervisor Name'),
		proof_of_request: Yup.array()
			.test('has-files', 'Proof of request is required', function validateFiles(value) {
				return value && Array.isArray(value) && value.length > 0;
			})
			.label('Proof of Request'),
		client_rate: Yup.number()
			.required('Client rate is required')
			.min(0.01, 'Client rate must be greater than 0')
			.test(
				DECIMAL_PLACES_TEST_NAME,
				`Client rate ${MAX_DECIMAL_PLACES_MSG}`,
				validateDecimalPlaces
			)
			.test(
				'is-greater',
				'Client rate must be greater than vendor rate',
				function validateClientRateGreater(value, context) {
					const parent = context?.parent || {};
					const { vendor_rate } = parent;
					if (!value || !vendor_rate) return true;
					return Number(value) > Number(vendor_rate);
				}
			)
			.label('Client Rate'),
		vendor_rate: Yup.number()
			.required('Vendor rate is required')
			.min(0.01, 'Vendor rate must be greater than 0')
			.test(
				DECIMAL_PLACES_TEST_NAME,
				`Vendor rate ${MAX_DECIMAL_PLACES_MSG}`,
				validateDecimalPlaces
			)
			.label('Vendor Rate'),
		// Transform empty string to undefined so validation passes for optional advance_amount
		advance_amount: Yup.number()
			.required('Advance amount is required')
			.transform((value, originalValue) => (originalValue === '' ? undefined : value))
			.min(0, 'Advance amount cannot be negative')
			.test(
				DECIMAL_PLACES_TEST_NAME,
				`Advance amount ${MAX_DECIMAL_PLACES_MSG}`,
				validateDecimalPlaces
			)
			.test(
				'min-percentage',
				'Advance amount must be at least 60% of vendor rate and not more than 100%',
				function validateAdvancePercentage(value, context) {
					const parent = context?.parent || {};
					const { vendor_rate } = parent;
					if (!value || !vendor_rate) return true;
					const percentage = (Number(value) / Number(vendor_rate)) * 100;
					return percentage >= 60 && percentage <= 100;
				}
			)
			.label('Advance'),
		proforma_invoice_number: Yup.string()
			.test(
				'require-with-document',
				'Pro-Forma Invoice Number is required when document is uploaded',
				function validateProformaNumber(value, context) {
					const parent = context?.parent || {};
					const { proforma_invoice } = parent;
					const hasDocument = proforma_invoice && proforma_invoice.length > 0;
					// If document is uploaded, number is required
					return !(hasDocument && !value);
				}
			)
			.label('Pro-Forma Invoice Number'),
		proforma_invoice: Yup.array()
			.test(
				'require-with-number',
				'Pro-Forma Invoice document is required when invoice number is entered',
				function validateProformaDocument(value, context) {
					const parent = context?.parent || {};
					const { proforma_invoice_number } = parent;
					const hasNumber = proforma_invoice_number && proforma_invoice_number.trim();
					const hasDocument = value && value.length > 0;
					// If number is entered, document is required
					return !(hasNumber && !hasDocument);
				}
			)
			.label('Pro-Forma Invoice'),
	});

	const calculateMargin = (clientRate, vendorRate) => {
		if (!clientRate || !vendorRate) return { margin: 0, marginPercent: 0 };
		const client = parseFloat(clientRate) || 0;
		const vendor = parseFloat(vendorRate) || 0;
		const margin = client - vendor;
		const marginPercent = client > 0 ? (margin / client) * 100 : 0;
		return {
			margin: margin.toFixed(2),
			marginPercent: marginPercent.toFixed(2),
		};
	};

	const calculateAdvancePercent = (advance, vendorRate) => {
		if (!advance || !vendorRate) return 0;
		const adv = parseFloat(advance) || 0;
		const vendor = parseFloat(vendorRate) || 0;
		return vendor > 0 ? ((adv / vendor) * 100).toFixed(2) : 0;
	};

	// Allow only today or tomorrow
	const disabledDate = (current) => {
		if (!current) return false;
		const today = moment().startOf('day');
		const maxDate = moment().add(1, 'day').endOf('day');
		return current < today || current > maxDate;
	};

	const temperatureLogsOptions = [
		{ value: true, label: 'Yes' },
		{ value: false, label: 'No' },
	];

	return (
		<FormContainer>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={onSubmit}
				enableReinitialize
				validateOnChange
				validateOnBlur
				innerRef={formRef}
			>
				{({
					values,
					errors,
					touched,
					setFieldValue,
					setFieldTouched,
					validateForm,
					isSubmitting,
				}) =>
					renderAdhocRequestFormFields({
						values,
						errors,
						touched,
						setFieldValue,
						setFieldTouched,
						validateForm,
						isSubmitting,
						nodeSequence,
						setNodeSequence,
						nodeSequenceModalVisible,
						setNodeSequenceModalVisible,
						isV2UILocal,
						isDisabled,
						clientData,
						projectData,
						vendorsData,
						vehicleTypes,
						supervisorData,
						sourceCity,
						destCity,
						businessUnitName,
						uploadingProof,
						uploadErrorProof,
						onClientChange,
						onProjectChange,
						onPincodeChange,
						onProofUpload,
						onVehicleChange,
						navigate,
						isEditMode,
						temperatureLogsOptions,
						disabledDate,
						calculateMargin,
						calculateAdvancePercent,
					})
				}
			</Formik>
		</FormContainer>
	);
}

AdhocRequestForm.propTypes = {
	formData: PropTypes.object, // Optional, defaults to empty object
	clientData: PropTypes.array,
	projectData: PropTypes.array,
	vendorsData: PropTypes.array,
	vehicleTypes: PropTypes.array,
	supervisorData: PropTypes.array,
	sourceCity: PropTypes.string,
	destCity: PropTypes.string,
	businessUnitName: PropTypes.string,
	uploadingProof: PropTypes.bool,
	uploadErrorProof: PropTypes.string,
	onVehicleChange: PropTypes.func,
	// uploadingProforma: PropTypes.bool,
	// uploadErrorProforma: PropTypes.string,
	formRef: PropTypes.any,
	onClientChange: PropTypes.func,
	onProjectChange: PropTypes.func,
	onPincodeChange: PropTypes.func,
	onProofUpload: PropTypes.func,
	// onProformaUpload: PropTypes.func,
	onSubmit: PropTypes.func,
	navigate: PropTypes.func,
	isEditMode: PropTypes.bool,
	requestData: PropTypes.object,
	isV2UI: PropTypes.bool,
};
